/**
 * Batch Control Log functionality for production runs
 * Handles view switching, pick list positioning, and font size controls
 */

import { once } from "~/jose_modern/once";

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
}

function setCookie(name, value) {
  document.cookie = `${name}=${value}; path=/`;
}

function initializeViewSwitching() {
  once(document, "bcl-view-switching", (doc) => {
    doc.addEventListener("click", (e) => {
      const button = e.target.closest("button[data-for]");
      if (!button) return;

      e.preventDefault();

      const target = button.dataset.for;
      const view = document.querySelector(`[data-bcl-view='${target}']`);

      if (!view) return;

      view.style.display = "block";
      const siblings = view.parentElement.querySelectorAll("[data-bcl-view]");
      siblings.forEach((sibling) => {
        if (sibling !== view) {
          sibling.style.display = "none";
        }
      });

      button.classList.add("is-success");
      button.classList.remove("is-outlined");

      const allButtons = document.querySelectorAll("button[data-for]");
      allButtons.forEach((btn) => {
        if (btn !== button) {
          btn.classList.remove("is-success");
          btn.classList.add("is-outlined");
        }
      });

      document.cookie = `recipal_bcl_view=${target}; path=/`;
    });
  });
}

function initializePickListToggle() {
  once(document, "bcl-pick-list-toggle", (doc) => {
    doc.addEventListener("click", (e) => {
      const button = e.target.closest("#toggle-pick-list");
      if (!button) return;

      e.preventDefault();

      const pickListFirst = document.getElementById("pick-list-first");
      const pickListLast = document.getElementById("pick-list-last");

      if (!pickListFirst || !pickListLast) return;

      const isInFirstPosition = button.closest("#pick-list-first") !== null;
      let htmlToMove = "";

      if (isInFirstPosition) {
        htmlToMove = pickListFirst.innerHTML;
        pickListFirst.innerHTML = "";

        const firstPageBreak = document.querySelector(".first-page-break");
        if (firstPageBreak) {
          firstPageBreak.classList.remove("page-break");
        }

        const beforePickListLast = pickListLast.previousElementSibling;
        if (beforePickListLast) {
          beforePickListLast.classList.add("page-break");
        }

        pickListLast.innerHTML = htmlToMove;

        const newButton = pickListLast.querySelector("#toggle-pick-list");
        if (newButton) {
          newButton.innerHTML = `
            <span class="icon">
              <i class="fa-solid fa-arrow-up"></i>
            </span>
            <span>Move Pick List to Beginning</span>
          `;
        }

        setCookie("recipal_bcl_pick_list_position", "end");
      } else {
        htmlToMove = pickListLast.innerHTML;
        pickListLast.innerHTML = "";

        const beforePickListLast = pickListLast.previousElementSibling;
        if (beforePickListLast) {
          beforePickListLast.classList.remove("page-break");
        }

        const firstPageBreak = document.querySelector(".first-page-break");
        if (firstPageBreak) {
          firstPageBreak.classList.add("page-break");
        }

        pickListFirst.innerHTML = htmlToMove;

        const newButton = pickListFirst.querySelector("#toggle-pick-list");
        if (newButton) {
          newButton.innerHTML = `
            <span class="icon">
              <i class="fa-solid fa-arrow-down"></i>
            </span>
            <span>Move Pick List To End</span>
          `;
        }

        setCookie("recipal_bcl_pick_list_position", "beginning");
      }
    });
  });
}

function initializeFontSizeControls() {
  once(document, "bcl-font-size-controls", (doc) => {
    doc.addEventListener("click", (e) => {
      const decreaseButton = e.target.closest("#decrease-font");
      if (decreaseButton) {
        e.preventDefault();
        adjustFontSize(-2);
        return;
      }

      const increaseButton = e.target.closest("#increase-font");
      if (increaseButton) {
        e.preventDefault();
        adjustFontSize(2);
      }
    });
  });
}

function initializePrintButton() {
  once(document, "bcl-print-button", (doc) => {
    doc.addEventListener("click", (e) => {
      if (!e.target.closest("#print-batch-control-log")) return;

      e.preventDefault();
      window.print();
    });
  });
}

function adjustFontSize(delta) {
  const currentDelta = parseInt(getCookie("recipal_bcl_font_delta") || "0", 10);
  const newDelta = currentDelta + delta;

  setCookie("recipal_bcl_font_delta", newDelta);
  applyFontSize(newDelta);
}

function applyFontSize(delta) {
  const elements = document.querySelectorAll("p, h1, h2, h3, h4, h5, h6, table, td, th");
  elements.forEach((element) => {
    const originalSize =
      parseFloat(window.getComputedStyle(element).fontSize) -
      parseInt(element.dataset.fontDelta || "0", 10);
    const newSize = Math.max(8, originalSize + delta);
    element.style.fontSize = `${newSize}px`;
    element.style.lineHeight = "140%";
    element.dataset.fontDelta = delta;
  });
}

function initializeFontSizeFromCookie() {
  const savedDelta = parseInt(getCookie("recipal_bcl_font_delta") || "0", 10);
  if (savedDelta !== 0) {
    applyFontSize(savedDelta);
  }
}

const scan = () => {
  initializeViewSwitching();
  initializePickListToggle();
  initializeFontSizeControls();
  initializeFontSizeFromCookie();
  initializePrintButton();
};

document.addEventListener("DOMContentLoaded", () => {
  if (document.querySelector("[data-bcl-view]")) {
    scan();
  }
});
